import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  Grid,
  GridColumn,
  GridPageChangeEvent,
  GridRowClickEvent,
} from "@progress/kendo-react-grid";
import { Input, NumericTextBox } from "@progress/kendo-react-inputs";
import CleanDatePicker from "src/dm/components/CleanDatePicker/CleanDatePicker";
import { Button } from "@progress/kendo-react-buttons";
import {
  AutoComplete,
  AutoCompleteChangeEvent,
  DropDownList,
  DropDownListChangeEvent,
} from "@progress/kendo-react-dropdowns";
import { useDeals, DealsQueryParams } from "src/dm/hooks/useDeals";
import { DealListItem } from "src/dm/interfaces/Deal";
import { useCurrentUser } from "src/dm/auth/useCurrentUser";
import {
  useCatMgrs,
  useConsumerMessageTypes,
  useCouponTypes,
  useDealOwnersList,
  useFamiliesLookup,
  useGroupsLookup,
  useSubgroupsLookup,
  useVendorsLookup,
  type CodeLabelRow,
} from "src/dm/hooks/useLookups";
import * as s from "./AdvancedSearch.css";

const toIso = (d: Date | null): string | undefined =>
  d
    ? new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString()
    : undefined;

// Hoisted out of the component so its identity is stable across renders —
// otherwise the useMemos that include it in their deps would recompute on
// every render (react-hooks/exhaustive-deps).
const ANY_ROW: CodeLabelRow = { code: "", label: "— Any —" };

const AdvancedSearch = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [draft, setDraft] = useState<DealsQueryParams>({});
  const [applied, setApplied] = useState<DealsQueryParams>({});
  // AP-vendor source selector — legacy AdvancedSearch lets internal users
  // toggle between "AP vendors that already appear on a deal" and "every
  // AP vendor in the system". The SPA defaults to the deal-vendor scope
  // (cheaper, behaviorally matches the legacy default), exposed here as a
  // hint passed back to the (currently free-text) AP vendor # input — the
  // backend filter is the same query column either way; the toggle only
  // changes the auto-complete corpus the SPA could lazily pull. Until the
  // typeahead lands the toggle is informational, but we surface it now so
  // the form has parity with the legacy radio set.
  const [apVendorScope, setApVendorScope] = useState<"deal" | "all">("deal");
  const currentUser = useCurrentUser();
  const isInternalUser = currentUser.data?.isInternalUser ?? false;

  // ---- Lookup hooks for the new DDL filters --------------------------------
  // catMgrs powers the legacy ddlCatMgr (Category sub-cat manager) DDL.
  // dealOwners powers ddlDealOwner. The other two back the legacy
  // ddlConsumerMessage / ddlCoupon dropdowns.
  const catMgrs = useCatMgrs();
  const dealOwners = useDealOwnersList();
  const consumerMessageTypes = useConsumerMessageTypes();
  const couponTypes = useCouponTypes();

  // ---- Item-level typeahead state (carry-forward SPA item 3) -------------
  // AdvancedSearch is a cross-deal search so it has no single deal's
  // catMgr/banner scope to feed the cascading lookups — pass `undefined` for
  // both and the endpoints fall back to the global set (per LookupsController
  // implementation; gated by the `cascadingEnabled` helper in useLookups).
  const itemScope = useMemo(() => ({ catMgrCode: undefined, banner: undefined }), []);
  const [vendorFilter, setVendorFilter] = useState<string>("");
  const [familyFilter, setFamilyFilter] = useState<string>("");
  const vendors = useVendorsLookup(itemScope, vendorFilter);
  const families = useFamiliesLookup(itemScope, familyFilter);
  const groups = useGroupsLookup(itemScope);
  const subgroups = useSubgroupsLookup(itemScope, draft.groupCode ?? null);

  // Kendo's controlled DropDownList wants the EXACT row object that lives in
  // `data`, not a primitive id. Each `*Data` array therefore prepends a
  // sentinel placeholder row so the user has a way to clear the picker back
  // to "no selection", and the `find()` lookups below project the form
  // state's primitive id onto that row.
  const catMgrData = useMemo(
    () => [
      { code: "", name: "— Any —" },
      ...(catMgrs.data ?? []),
    ],
    [catMgrs.data]
  );
  const dealOwnerData = useMemo(
    () => [
      { userId: "", fullName: "— Any —" },
      ...(dealOwners.data ?? []),
    ],
    [dealOwners.data]
  );
  const consumerMessageData = useMemo(
    () => [
      { id: -1, label: "— Any —" },
      ...(consumerMessageTypes.data ?? []),
    ],
    [consumerMessageTypes.data]
  );
  const couponData = useMemo(
    () => [
      { id: -1, label: "— Any —" },
      ...(couponTypes.data ?? []),
    ],
    [couponTypes.data]
  );

  // Sentinel "— Any —" row for the group / sub-group DropDownLists. Kendo's
  // controlled DropDownList wants an actual row object for the selection so
  // we prepend a sentinel (ANY_ROW above) with an empty code, and translate
  // `code === ""` back to null on change.
  const groupRows = useMemo<CodeLabelRow[]>(
    () => [ANY_ROW, ...(groups.data?.items ?? [])],
    [groups.data]
  );
  const subgroupRows = useMemo<CodeLabelRow[]>(
    () => [ANY_ROW, ...(subgroups.data?.items ?? [])],
    [subgroups.data]
  );

  // Vendor/Family typeaheads are AutoComplete (free text + per-keystroke
  // server filter), matching the AddItem pattern. We map labels to the
  // numeric code on selection AND fall back to parsing the raw text as a
  // number so users who type a vendor number directly still get the right
  // result.
  const vendorLabels = useMemo<string[]>(
    () => (vendors.data?.items ?? []).map((v) => v.label),
    [vendors.data]
  );
  const familyLabels = useMemo<string[]>(
    () => (families.data?.items ?? []).map((f) => f.label),
    [families.data]
  );

  const onVendorChange = (e: AutoCompleteChangeEvent) => {
    const text = String(e.value ?? "").trim();
    setVendorFilter(text);
    if (!text) {
      setField("itemVendorNumber", null);
      return;
    }
    const match = (vendors.data?.items ?? []).find(
      (v) => v.label.toLowerCase() === text.toLowerCase() || v.code === text
    );
    if (match) {
      setField("itemVendorNumber", Number(match.code));
    } else {
      const asNum = Number(text);
      setField(
        "itemVendorNumber",
        Number.isFinite(asNum) && asNum > 0 ? asNum : null
      );
    }
  };

  const onFamilyChange = (e: AutoCompleteChangeEvent) => {
    const text = String(e.value ?? "").trim();
    setFamilyFilter(text);
    if (!text) {
      setField("familyCode", null);
      return;
    }
    const match = (families.data?.items ?? []).find(
      (f) => f.label.toLowerCase() === text.toLowerCase() || f.code === text
    );
    if (match) {
      setField("familyCode", Number(match.code));
    } else {
      const asNum = Number(text);
      setField(
        "familyCode",
        Number.isFinite(asNum) && asNum > 0 ? asNum : null
      );
    }
  };

  const findGroupRow = (
    rows: CodeLabelRow[],
    code: number | null | undefined
  ): CodeLabelRow => {
    if (code == null) return rows[0];
    return rows.find((r) => r.code === String(code)) ?? rows[0];
  };

  const onGroupChange = (e: DropDownListChangeEvent) => {
    const row = e.value as CodeLabelRow | null;
    const code = row?.code ?? "";
    if (!code) {
      // Picking "— Any —" clears the sub-group too so the cascade stays
      // consistent (a stale subgroup with no group makes no sense).
      setDraft((p) => ({ ...p, groupCode: null, subgroupCode: null }));
    } else {
      setDraft((p) => ({
        ...p,
        groupCode: Number(code),
        // Clear subgroup whenever group changes — the subgroup list
        // re-loads under the new group and the previously-chosen value
        // may no longer be valid.
        subgroupCode: null,
      }));
    }
  };

  const onSubgroupChange = (e: DropDownListChangeEvent) => {
    const row = e.value as CodeLabelRow | null;
    const code = row?.code ?? "";
    setField("subgroupCode", code ? Number(code) : null);
  };

  const { data, isLoading, isError, error } = useDeals({
    ...applied,
    page,
    pageSize,
  });

  const setField = <K extends keyof DealsQueryParams>(
    key: K,
    value: DealsQueryParams[K]
  ) => setDraft((p) => ({ ...p, [key]: value }));

  const handleApply = () => {
    setPage(1);
    setApplied(draft);
  };
  const handleReset = () => {
    setDraft({});
    setApplied({});
    setPage(1);
    // Item-level typeaheads keep their own text state — clear it so the
    // AutoComplete inputs don't show stale text after a reset.
    setVendorFilter("");
    setFamilyFilter("");
  };

  const handlePageChange = (e: GridPageChangeEvent) => {
    setPage(Math.floor(e.page.skip / e.page.take) + 1);
    setPageSize(e.page.take);
  };

  const handleRowClick = (e: GridRowClickEvent) => {
    const row = e.dataItem as DealListItem;
    if (row?.dealId) navigate(`/dm/deals/${row.dealId}`);
  };

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <div className={s.wrap}>
      <section className={s.filterCard}>
        <h2 className={s.filterTitle}>Advanced Search</h2>
        <div className={s.filterGrid}>
          <label>
            <span className={s.fieldLabel}>Deal ID</span>
            <NumericTextBox
              value={draft.dealId ?? null}
              onChange={(e) => setField("dealId", e.value ?? null)}
            />
          </label>
          <label>
            <span className={s.fieldLabel}>Description contains</span>
            <Input
              value={draft.description ?? ""}
              onChange={(e) =>
                setField("description", String(e.target.value ?? ""))
              }
            />
          </label>
          <label>
            <span className={s.fieldLabel}>Status</span>
            <Input
              value={draft.status ?? ""}
              onChange={(e) => setField("status", String(e.target.value ?? ""))}
              placeholder="NEW / SUBMIT / COMPLETE / …"
            />
          </label>
          <label>
            <span className={s.fieldLabel}>Banner</span>
            <Input
              value={draft.bannerCode ?? ""}
              onChange={(e) => setField("bannerCode", String(e.target.value ?? ""))}
              placeholder="GE / MA / …"
            />
          </label>
          <label>
            {/*
              Legacy ddlCatMgr — Category (sub-cat manager) DDL. Previously a
              free-text NumericTextBox; we now bind to the CategoryManager
              list from /lookups/cat-mgrs so users see "Brown 12, Kathy" etc.
              instead of memorising 2-digit codes.
            */}
            <span className={s.fieldLabel}>Category (Cat Mgr)</span>
            <DropDownList
              data={catMgrData}
              textField="name"
              dataItemKey="code"
              loading={catMgrs.isLoading}
              value={
                catMgrData.find(
                  (o) => o.code === String(draft.categoryManagerCode ?? "")
                ) ?? catMgrData[0]
              }
              onChange={(e: DropDownListChangeEvent) => {
                const row = e.target.value as { code: string };
                setField(
                  "categoryManagerCode",
                  row.code ? Number(row.code) : null
                );
              }}
            />
          </label>
          <label>
            <span className={s.fieldLabel}>Deal Type ID</span>
            <NumericTextBox
              value={draft.dealTypeId ?? null}
              onChange={(e) => setField("dealTypeId", e.value ?? null)}
            />
          </label>
          <label>
            <span className={s.fieldLabel}>Vendor / Broker ID</span>
            <NumericTextBox
              value={draft.vendorBrokerId ?? null}
              onChange={(e) => setField("vendorBrokerId", e.value ?? null)}
            />
          </label>
          <label>
            {/*
              Legacy ddlDealOwner — pre-populated DDL of every user that has
              ever owned a deal. Backed by /lookups/deal-owners-list (DISTINCT
              OWNR_USER_ID join RP_USER_PROFILE). Replaces the free-text input
              that required users to type DOMAIN\username exactly.
            */}
            <span className={s.fieldLabel}>Deal Owner</span>
            <DropDownList
              data={dealOwnerData}
              textField="fullName"
              dataItemKey="userId"
              loading={dealOwners.isLoading}
              value={
                dealOwnerData.find(
                  (o) => o.userId === (draft.ownerUserId ?? "")
                ) ?? dealOwnerData[0]
              }
              onChange={(e: DropDownListChangeEvent) => {
                const row = e.target.value as { userId: string };
                setField("ownerUserId", row.userId || undefined);
              }}
            />
          </label>
          <label>
            {/*
              Legacy ddlConsumerMessage — single-select DDL (Bonus Fuelperks,
              Catalina, Save Instantly, …). Backend translates this into an
              EXISTS subquery against DEAL_CONSUMER_MESSAGE keyed by CMST_ID.
            */}
            <span className={s.fieldLabel}>Consumer Message</span>
            <DropDownList
              data={consumerMessageData}
              textField="label"
              dataItemKey="id"
              loading={consumerMessageTypes.isLoading}
              value={
                consumerMessageData.find(
                  (o) => o.id === (draft.consumerMessageTypeId ?? -1)
                ) ?? consumerMessageData[0]
              }
              onChange={(e: DropDownListChangeEvent) => {
                const row = e.target.value as { id: number };
                setField(
                  "consumerMessageTypeId",
                  row.id === -1 ? null : row.id
                );
              }}
            />
          </label>
          <label>
            {/*
              Legacy ddlCoupon — single-select DDL (FSI Coupon, GE Specific
              FSI, …). Translates to an EXISTS subquery against DEAL_COUPON
              keyed by CPNT_ID.
            */}
            <span className={s.fieldLabel}>Coupon</span>
            <DropDownList
              data={couponData}
              textField="label"
              dataItemKey="id"
              loading={couponTypes.isLoading}
              value={
                couponData.find((o) => o.id === (draft.couponTypeId ?? -1)) ??
                couponData[0]
              }
              onChange={(e: DropDownListChangeEvent) => {
                const row = e.target.value as { id: number };
                setField("couponTypeId", row.id === -1 ? null : row.id);
              }}
            />
          </label>
          <label>
            <span className={s.fieldLabel}>Ad Start from</span>
            <CleanDatePicker
              value={draft.adStartFrom ? new Date(draft.adStartFrom) : null}
              onChange={(e) => setField("adStartFrom", toIso(e.value))}
            />
          </label>
          <label>
            <span className={s.fieldLabel}>Ad Start to</span>
            <CleanDatePicker
              value={draft.adStartTo ? new Date(draft.adStartTo) : null}
              onChange={(e) => setField("adStartTo", toIso(e.value))}
            />
          </label>
          <label>
            <span className={s.fieldLabel}>Promo Start from</span>
            <CleanDatePicker
              value={draft.promoStartFrom ? new Date(draft.promoStartFrom) : null}
              onChange={(e) => setField("promoStartFrom", toIso(e.value))}
            />
          </label>
          <label>
            <span className={s.fieldLabel}>Promo Start to</span>
            <CleanDatePicker
              value={draft.promoStartTo ? new Date(draft.promoStartTo) : null}
              onChange={(e) => setField("promoStartTo", toIso(e.value))}
            />
          </label>
          <label>
            <span className={s.fieldLabel}>Promo End from</span>
            <CleanDatePicker
              value={draft.promoEndFrom ? new Date(draft.promoEndFrom) : null}
              onChange={(e) => setField("promoEndFrom", toIso(e.value))}
            />
          </label>
          <label>
            <span className={s.fieldLabel}>Promo End to</span>
            <CleanDatePicker
              value={draft.promoEndTo ? new Date(draft.promoEndTo) : null}
              onChange={(e) => setField("promoEndTo", toIso(e.value))}
            />
          </label>
          <label>
            <span className={s.fieldLabel}>Ad Plan Code</span>
            <Input
              value={draft.adPlanCode ?? ""}
              onChange={(e) => setField("adPlanCode", String(e.target.value ?? ""))}
              placeholder="P / U / …"
            />
          </label>
          <label>
            <span className={s.fieldLabel}>Ad Strength</span>
            <Input
              value={draft.adStrength ?? ""}
              onChange={(e) => setField("adStrength", String(e.target.value ?? ""))}
            />
          </label>
          <label>
            <span className={s.fieldLabel}>Ad Block ID</span>
            <NumericTextBox
              value={draft.adBlockId ?? null}
              onChange={(e) => setField("adBlockId", e.value ?? null)}
            />
          </label>
          <label>
            <span className={s.fieldLabel}>
              A/P Vendor #
              {isInternalUser && (
                <span
                  style={{ marginLeft: "0.4rem", fontSize: "0.7rem", color: "#64748b", fontWeight: 400 }}
                  title="Toggle the AP-vendor typeahead source. Deal-vendor restricts the corpus to vendors already attached to a deal; All AP vendors searches the entire AP vendor table."
                >
                  ({apVendorScope === "deal" ? "deal vendors" : "all AP vendors"})
                </span>
              )}
            </span>
            <Input
              value={draft.apVendorNumber ?? ""}
              onChange={(e) => setField("apVendorNumber", String(e.target.value ?? ""))}
            />
            {isInternalUser && (
              <div style={{ display: "flex", gap: "0.75rem", fontSize: "0.75rem", marginTop: "0.2rem", color: "#475569" }}>
                <label style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                  <input
                    type="radio"
                    name="apVendorScope"
                    checked={apVendorScope === "deal"}
                    onChange={() => setApVendorScope("deal")}
                  />
                  Deal vendors
                </label>
                <label style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                  <input
                    type="radio"
                    name="apVendorScope"
                    checked={apVendorScope === "all"}
                    onChange={() => setApVendorScope("all")}
                  />
                  All AP vendors
                </label>
              </div>
            )}
          </label>
        </div>

        <div style={{ marginTop: "0.75rem", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.75rem", alignItems: "end" }}>
          <label>
            <span className={s.fieldLabel}>Date column</span>
            <select
              value={draft.dateColumn ?? ""}
              onChange={(e) => setField("dateColumn", e.target.value || undefined)}
              style={{ width: "100%", padding: "0.4rem", border: "1px solid #cbd5e1", borderRadius: 4, background: "white" }}
            >
              <option value="">— pick a date column —</option>
              <option value="PROMO_START">Promo Retail Start</option>
              <option value="PROMO_END">Promo Retail End</option>
              <option value="AD_START">Ad Start</option>
              <option value="AD_END">Ad End</option>
              <option value="SCAN_START">Scan Start</option>
              <option value="SCAN_END">Scan End</option>
              <option value="BUY_FIRST">Buy/Order First</option>
              <option value="BUY_LAST">Buy/Order Last</option>
              <option value="SHIP_FIRST">First Ship</option>
              <option value="SHIP_LAST">Last Ship</option>
              <option value="CRC_START">C/RC Start</option>
              <option value="CRC_END">C/RC End</option>
              <option value="REFLECT_START">Reflect Start</option>
              <option value="REFLECT_END">Reflect End</option>
            </select>
          </label>
          <label>
            <span className={s.fieldLabel}>From</span>
            <CleanDatePicker
              value={draft.dateFrom ? new Date(draft.dateFrom) : null}
              onChange={(e) => setField("dateFrom", toIso(e.value))}
              disabled={!draft.dateColumn}
            />
          </label>
          <label>
            <span className={s.fieldLabel}>To</span>
            <CleanDatePicker
              value={draft.dateTo ? new Date(draft.dateTo) : null}
              onChange={(e) => setField("dateTo", toIso(e.value))}
              disabled={!draft.dateColumn}
            />
          </label>
        </div>

        <div style={{ marginTop: "0.75rem" }}>
          <div style={{ fontSize: "0.75rem", color: "#475569", marginBottom: "0.25rem" }}>
            Allowance types — any deal with the checked allowance flag(s) (Y)
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", fontSize: "0.85rem" }}>
              <input type="checkbox" checked={!!draft.hasScanback} onChange={(e) => setField("hasScanback", e.target.checked || undefined)} />
              Scanback
            </label>
            <label style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", fontSize: "0.85rem" }}>
              <input type="checkbox" checked={!!draft.hasOffInvoice} onChange={(e) => setField("hasOffInvoice", e.target.checked || undefined)} />
              Off Invoice
            </label>
            <label style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", fontSize: "0.85rem" }}>
              <input type="checkbox" checked={!!draft.hasBillBack} onChange={(e) => setField("hasBillBack", e.target.checked || undefined)} />
              Billback
            </label>
            <label style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", fontSize: "0.85rem" }}>
              <input type="checkbox" checked={!!draft.hasCountRecount} onChange={(e) => setField("hasCountRecount", e.target.checked || undefined)} />
              Count/Recount
            </label>
            {/* Pass-Through is an internal-only allowance type in the
                legacy form (see audit 11 §2 — Pass-Through is hidden from
                external users). Mirror that gating here. */}
            {isInternalUser && (
              <label style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", fontSize: "0.85rem" }}>
                <input type="checkbox" checked={!!draft.hasPassThru} onChange={(e) => setField("hasPassThru", e.target.checked || undefined)} />
                Pass Through
              </label>
            )}
          </div>
        </div>

        <details style={{ marginTop: "1rem", border: "1px solid #e2e8f0", borderRadius: 6, padding: "0.5rem 0.75rem" }}>
          <summary style={{ cursor: "pointer", fontWeight: 600, fontSize: "0.9rem" }}>
            Item-level filters
          </summary>
          <div className={s.filterGrid} style={{ marginTop: "0.5rem" }}>
            <label>
              <span className={s.fieldLabel}>Item UPC</span>
              <NumericTextBox value={draft.itemUpc ?? null} onChange={(e) => setField("itemUpc", e.value ?? null)} format="0" spinners={false} />
            </label>
            <label>
              <span className={s.fieldLabel}>Item Code</span>
              <NumericTextBox value={draft.itemCode ?? null} onChange={(e) => setField("itemCode", e.value ?? null)} format="0" spinners={false} />
            </label>
            <label>
              <span className={s.fieldLabel}>Case UPC</span>
              <NumericTextBox value={draft.caseUpc ?? null} onChange={(e) => setField("caseUpc", e.value ?? null)} format="0" spinners={false} />
            </label>
            <label>
              <span className={s.fieldLabel}>Item Description</span>
              <Input value={draft.itemDescription ?? ""} onChange={(e) => setField("itemDescription", String(e.target.value ?? ""))} />
            </label>
            {/*
              Item-level typeaheads (carry-forward SPA item 3). Replaces the
              previous NumericTextBox inputs which forced users to know the
              numeric codes by heart. Vendor + Family are AutoComplete (server-
              side LIKE search via /lookups/vendors and /lookups/families)
              because the underlying domains run into the thousands of rows;
              Group + Sub-Group are full DropDownList (small enough that the
              whole list fits client-side). All four lookups are called with
              `catMgrCode`/`banner` undefined — AdvancedSearch is a cross-deal
              search, so the endpoints return the global set rather than a
              single-deal scope.
            */}
            <label>
              <span className={s.fieldLabel}>
                Family {families.isFetching && "…"}
              </span>
              <AutoComplete
                data={familyLabels}
                value={familyFilter}
                onChange={onFamilyChange}
                loading={families.isFetching}
                placeholder="Type family code or description"
              />
            </label>
            <label>
              <span className={s.fieldLabel}>Group</span>
              <DropDownList
                data={groupRows}
                textField="label"
                dataItemKey="code"
                value={findGroupRow(groupRows, draft.groupCode)}
                onChange={onGroupChange}
                loading={groups.isLoading}
                filterable={true}
              />
            </label>
            <label>
              <span className={s.fieldLabel}>Sub-Group</span>
              <DropDownList
                data={subgroupRows}
                textField="label"
                dataItemKey="code"
                value={findGroupRow(subgroupRows, draft.subgroupCode)}
                onChange={onSubgroupChange}
                loading={subgroups.isLoading}
                filterable={true}
              />
            </label>
            <label>
              <span className={s.fieldLabel}>
                Item Vendor {vendors.isFetching && "…"}
              </span>
              <AutoComplete
                data={vendorLabels}
                value={vendorFilter}
                onChange={onVendorChange}
                loading={vendors.isFetching}
                placeholder="Type vendor name or number"
              />
            </label>
          </div>
        </details>

        <div style={{ display: "flex", gap: "1rem", marginTop: "0.75rem", flexWrap: "wrap" }}>
          {/* Internal-only filter only makes sense for internal users —
              external users cannot see internal-only deals at all, so the
              filter has no effect. Legacy hides the checkbox; we follow. */}
          {isInternalUser && (
            <label style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", fontSize: "0.9rem" }}>
              <input
                type="checkbox"
                checked={!!draft.internalOnly}
                onChange={(e) => setField("internalOnly", e.target.checked || undefined)}
              />
              Internal only
            </label>
          )}
          <label style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", fontSize: "0.9rem" }}>
            <input
              type="checkbox"
              checked={!!draft.termSheetOnly}
              onChange={(e) => setField("termSheetOnly", e.target.checked || undefined)}
            />
            Term sheet only
          </label>
          <label style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", fontSize: "0.9rem" }}>
            <input
              type="checkbox"
              checked={!!draft.rewardOnly}
              onChange={(e) => setField("rewardOnly", e.target.checked || undefined)}
            />
            Reward promotions only
          </label>
        </div>

        <div style={{ marginTop: "0.75rem" }}>
          <div style={{ fontSize: "0.75rem", color: "#475569", marginBottom: "0.25rem" }}>
            Status — check any to include (uses IN clause)
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            {(["NEW", "SUBMIT", "REVIEW", "APPROVE", "AUTHORIZE", "REJECT", "MODIFY", "PREPARE", "START", "COMPLETE", "RESCIND", "UNLOCK", "CHGPNDG"] as const).map((s) => {
              const isOn = draft.statuses?.includes(s) ?? false;
              return (
                <label key={s} style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", fontSize: "0.85rem" }}>
                  <input
                    type="checkbox"
                    checked={isOn}
                    onChange={(e) => {
                      const next = new Set(draft.statuses ?? []);
                      if (e.target.checked) next.add(s);
                      else next.delete(s);
                      setField("statuses", next.size > 0 ? Array.from(next) : undefined);
                    }}
                  />
                  {s}
                </label>
              );
            })}
          </div>
        </div>

        <div className={s.actionRow}>
          <Button type="button" onClick={handleReset}>
            Reset
          </Button>
          <Button type="button" themeColor="primary" onClick={handleApply}>
            Apply filters
          </Button>
        </div>
      </section>

      <section className={s.resultsCard}>
        {isLoading && <p>Searching…</p>}
        {isError && (
          <p style={{ color: "#b91c1c" }}>
            {error instanceof Error ? error.message : "Search failed"}
          </p>
        )}
        {!isLoading && !isError && (
          <>
            <p style={{ color: "#555", margin: 0, marginBottom: "0.5rem" }}>
              {total.toLocaleString()} deal{total === 1 ? "" : "s"} match
            </p>
            <Grid
              data={items}
              skip={(page - 1) * pageSize}
              take={pageSize}
              total={total}
              pageable={{ pageSizes: [10, 25, 50, 100] }}
              onPageChange={handlePageChange}
              onRowClick={handleRowClick}
              style={{ cursor: "pointer" }}
            >
              <GridColumn field="dealId" title="Deal ID" width="100px" />
              <GridColumn field="description" title="Description" />
              <GridColumn field="bannerCode" title="Banner" width="100px" />
              <GridColumn field="status" title="Status" width="140px" />
              <GridColumn field="adStartDate" title="Ad Start" width="120px" />
              <GridColumn field="adEndDate" title="Ad End" width="120px" />
              <GridColumn field="ownerUserId" title="Owner" width="180px" />
            </Grid>
          </>
        )}
      </section>
    </div>
  );
};

export default AdvancedSearch;
